import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MAIN_SITE_URL } from '@/mocks/site';

type FooterLink =
  | { kind: 'route'; label: string; to: string }
  | { kind: 'anchor'; label: string; href: string }
  | { kind: 'external'; label: string; href: string };

export default function Footer() {
  const { t } = useTranslation();

  const resourceLinks: FooterLink[] = [
    { kind: 'route', label: t('common.footer.resourcesGuides'), to: '/usfans-spreadsheet-guide' },
    { kind: 'anchor', label: t('common.footer.resourcesCategories'), href: '#categories' },
    { kind: 'anchor', label: t('common.footer.resourcesSafety'), href: '#safety' },
    { kind: 'anchor', label: t('common.footer.resourcesFaq'), href: '#faq' },
  ];

  const companyLinks: FooterLink[] = [
    { kind: 'anchor', label: t('common.footer.about'), href: '#about' },
    { kind: 'anchor', label: t('common.footer.editorialPolicy'), href: '#about' },
    { kind: 'external', label: t('common.footer.contact'), href: MAIN_SITE_URL },
  ];

  const legalLinks: FooterLink[] = [
    { kind: 'anchor', label: t('common.footer.privacy'), href: '#about' },
    { kind: 'anchor', label: t('common.footer.terms'), href: '#about' },
    { kind: 'anchor', label: t('common.footer.disclaimer'), href: '#about' },
  ];

  const renderLink = (link: FooterLink) => {
    if (link.kind === 'route') {
      return (
        <Link to={link.to} className="text-sm text-foreground-600 transition-colors hover:text-foreground-950">
          {link.label}
        </Link>
      );
    }
    if (link.kind === 'external') {
      return (
        <a
          href={link.href}
          target="_blank"
          rel="noopener nofollow"
          className="text-sm text-foreground-600 transition-colors hover:text-foreground-950"
        >
          {link.label}
        </a>
      );
    }
    return (
      <Link to={`/${link.href}`} className="text-sm text-foreground-600 transition-colors hover:text-foreground-950">
        {link.label}
      </Link>
    );
  };

  return (
    <footer className="border-t border-background-200 bg-secondary-50">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-grid-fill text-base" />
              </span>
              <span className="font-heading text-base font-bold text-foreground-950">{t('common.brand')}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">{t('common.footer.tagline')}</p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950">
              <a href="#resources" className="hover:underline">
                {t('common.footer.resources')}
              </a>
            </h4>
            <ul className="mt-4 flex flex-col gap-2.5">
              {resourceLinks.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950">
              <a href="#company" className="hover:underline">
                {t('common.footer.company')}
              </a>
            </h4>
            <ul className="mt-4 flex flex-col gap-2.5">
              {companyLinks.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground-950">
              <a href="#legal" className="hover:underline">
                {t('common.footer.legal')}
              </a>
            </h4>
            <ul className="mt-4 flex flex-col gap-2.5">
              {legalLinks.map((link) => (
                <li key={link.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-background-200 pt-6 sm:flex-row">
          <p className="text-xs text-foreground-500">
            © {new Date().getFullYear()} {t('common.brand')}. {t('common.footer.rights')}
          </p>
          <p className="text-xs text-foreground-500">
            <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="hover:underline">
              {t('common.cta.explore')}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}